import { useState, useCallback } from 'react';
import { validateFiles } from '../utils/validators';

export const useFileUpload = (maxFiles: number = 10) => {
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const generatePreview = (file: File): Promise<string> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.readAsDataURL(file);
    });
  };

  const addFiles = useCallback(
    async (newFiles: File[]) => {
      setError(null);

      if (files.length + newFiles.length > maxFiles) {
        setError(`Maximum ${maxFiles} files allowed`);
        return;
      }

      const validation = validateFiles([...files, ...newFiles]);
      if (!validation.valid) {
        setError(validation.error || 'Invalid files');
        return;
      }

      const allFiles = [...files, ...newFiles];
      setFiles(allFiles);

      const newPreviews = await Promise.all(newFiles.map(generatePreview));
      setPreviews([...previews, ...newPreviews]);
    },
    [files, previews, maxFiles]
  );

  const removeFile = useCallback(
    (index: number) => {
      const newFiles = files.filter((_, i) => i !== index);
      const newPreviews = previews.filter((_, i) => i !== index);
      setFiles(newFiles);
      setPreviews(newPreviews);
      setError(null);
    },
    [files, previews]
  );

  const clearFiles = useCallback(() => {
    setFiles([]);
    setPreviews([]);
    setError(null);
  }, []);

  return {
    files,
    previews,
    error,
    addFiles,
    removeFile,
    clearFiles,
  };
};

